/** Shared Chart.js fragments reused across the chart components, so the tooltip
 *  formatting, legend style, zoom behaviour and dark/light palette stay in one place. */

import { linkZoomComplete } from './chart-link.plugin';

/** Time range of the expanded charts: 'day' is usually the live 24 h window, the rest the archive. */
export type ChartRange = 'day' | 'week' | 'month' | 'year';

/** Range buttons (Tag/Woche/Monat/Jahr). `label` is an i18n key: render it through the translate
 *  pipe (or `translate.instant` in a getter) so it follows a language switch. */
export const CHART_RANGES: readonly { readonly key: ChartRange; readonly label: string }[] = [
  { key: 'day',   label: 'RANGE_DAY' },
  { key: 'week',  label: 'RANGE_WEEK' },
  { key: 'month', label: 'RANGE_MONTH' },
  { key: 'year',  label: 'RANGE_YEAR' },
];

/** Span of each range in ms. */
export const CHART_RANGE_MS: Readonly<Record<ChartRange, number>> = {
  day:   24 * 3600 * 1000,
  week:  7 * 24 * 3600 * 1000,
  month: 30 * 24 * 3600 * 1000,
  year:  365 * 24 * 3600 * 1000,
};

/** Colour of every set-point (Soll) line — dashed orange, distinct from the actual (Ist) lines'
 *  colours, readable on the light and the dark card surface. */
export function setpointColor(darkMode: boolean): string {
  return darkMode ? '#ffa94d' : '#e8590c';
}

/** Flow temperature (Ist) colour of heating circuit `i` in a chart that shows several circuits — never
 *  the set-point orange; lighter shades on the dark theme. */
export function heatingCircuitColor(i: number, darkMode: boolean): string {
  // Only where several circuits share one chart (the 📈 heating modal, the board's "all values"):
  // a chart of one circuit uses circuitColor(), the same for every circuit.
  const colors = darkMode ? ['#198754', '#a370f7', '#e685b5', '#20c997'] : ['#198754', '#6610f2', '#d63384', '#20c997'];
  return colors[i % colors.length];
}

/** Flow temperature (Ist) colour of a heating circuit in its own chart and on its card: the same for
 *  every circuit, so their charts and legends read alike (Ist green, Soll dashed orange, pump grey). */
export function circuitColor(_darkMode: boolean): string {
  return '#198754';
}

/** The boiler's exhaust temperature line: brown, solid (dashed orange means Soll). */
export function exhaustColor(darkMode: boolean): string {
  return darkMode ? '#bcaaa4' : '#8d6e63';
}

export function chartPalette(darkMode: boolean): { textColor: string; gridColor: string } {
  return {
    textColor: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
    gridColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
  };
}

/** Locale of the UI language for dates and numbers (AppComponent keeps <html lang> on the UI language). */
export function uiLocale(): string {
  return document.documentElement.lang === 'de' ? 'de-DE' : 'en-GB';
}

/** Tooltip title: the hovered point's time as "HH:mm" in the UI locale. */
export function timeTooltipTitle(items: any[]): string {
  const ts = items[0]?.parsed.x;
  return ts != null
    ? new Date(ts).toLocaleTimeString(uiLocale(), { hour: '2-digit', minute: '2-digit' })
    : '';
}

/** Legend label config that renders each dataset as a coloured line swatch. */
export function lineLegendLabels(textColor: string): any {
  return {
    color: textColor,
    usePointStyle: true,
    generateLabels: (chart: any) =>
      chart.data.datasets.map((ds: any, i: number) => ({
        text:        ds.label ?? '',
        // a shaded area (ds.legendBox: the burner) as a filled box, every other dataset as its line
        fillStyle:   ds.legendBox ? (ds.legendFill ?? ds.backgroundColor) : ds.borderColor,
        strokeStyle: ds.borderColor,
        lineDash:    ds.legendBox ? [] : ds.borderDash ?? [],
        lineWidth:   ds.legendBox ? 1 : 2,
        hidden:      !chart.isDatasetVisible(i),
        datasetIndex: i,
        pointStyle:  ds.legendBox ? 'rect' : 'line',
        fontColor:   textColor,
      })),
  };
}

/** The standard drag/pinch x-axis zoom plugin config. The zoom window of a linked chart
 *  (options.plugins.deepLink.id) goes into the deep link; `onComplete` runs after a zoom, a reset
 *  (chartjs-plugin-zoom calls it from resetZoom too) and when a linked window is restored. */
export function dragZoom(onComplete: () => void = () => {}): any {
  return {
    zoom: {
      drag: { enabled: true, backgroundColor: 'rgba(13,110,253,0.1)', borderColor: 'rgba(13,110,253,0.3)', borderWidth: 1 },
      pinch: { enabled: true },
      mode: 'x' as const,
      onZoomComplete: ({ chart }: { chart: any }) => { linkZoomComplete(chart); onComplete(); },
    },
  };
}

/** Parse a range keyword (e.g. from a deep link); null if it is none of CHART_RANGES. */
export function parseChartRange(v: string | null | undefined): ChartRange | null {
  return CHART_RANGES.some(r => r.key === v) ? v as ChartRange : null;
}

/**
 * The range to load so a linked zoom window is inside the data: `range` itself if its window
 * (ending now) reaches back to the zoom's start, else the shortest range that does ('year' at most).
 */
export function coveringRange(range: ChartRange, zoom: { from: number; to: number } | null, now = Date.now()): ChartRange {
  if (!zoom) return range;
  const order: ChartRange[] = ['day', 'week', 'month', 'year'];
  for (const r of order.slice(order.indexOf(range))) {
    if (now - CHART_RANGE_MS[r] <= zoom.from) return r;
  }
  return 'year';
}

/** Date formats of the axis ticks: day + month ("21.09." / "21/09"), month + year ("Okt. 25" / "Oct 25"). */
const TICK_DATE_FORMATS = {
  dayMonth:  { day: '2-digit', month: '2-digit' },
  monthYear: { month: 'short', year: '2-digit' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;
const tickDateFormatters = new Map<string, Intl.DateTimeFormat>();

/** The (cached) formatter of a tick date format in the current UI locale. */
function tickDateFormat(kind: keyof typeof TICK_DATE_FORMATS): Intl.DateTimeFormat {
  const locale = uiLocale();
  const key = `${locale}|${kind}`;
  let f = tickDateFormatters.get(key);
  if (!f) tickDateFormatters.set(key, f = new Intl.DateTimeFormat(locale, TICK_DATE_FORMATS[kind]));
  return f;
}

/**
 * x-axis tick formatter appropriate to the visible span: HH:00 within ~2 days, day + month up to
 * ~3 months, otherwise month + year — dates in the UI locale (looked up per tick from a cache, so a
 * language switch shows on the next chart update). Used for the expanded charts whose range can be
 * Tag/Woche/Monat/Jahr.
 */
export function timeAxisTick(minMs: number, maxMs: number): (val: number | string) => string | null {
  const span = maxMs - minMs;
  const day = 24 * 3600 * 1000;
  if (span <= 2 * day) {
    return (val) => {
      const d = new Date(val as number);
      return d.getMinutes() === 0 ? `${String(d.getHours()).padStart(2, '0')}:00` : null;
    };
  }
  const kind = span <= 92 * day ? 'dayMonth' : 'monthYear';
  return (val) => tickDateFormat(kind).format(val as number);
}

/** Tick positions of a (zoomed) time axis min..max: whole local hours within ~2 days, noons up to
 *  ~3 months, else month starts — so a zoomed window gets readable, aligned labels. */
export function alignedTimeTicks(min: number, max: number): number[] {
  const day = 24 * 3600 * 1000;
  const span = max - min;
  const d = new Date(min);
  let step: () => void;
  if (span <= 2 * day)       { d.setMinutes(0, 0, 0);   step = () => d.setHours(d.getHours() + 1); }
  else if (span <= 92 * day) { d.setHours(12, 0, 0, 0); step = () => d.setDate(d.getDate() + 1); }
  else                       { d.setHours(0, 0, 0, 0); d.setDate(1); step = () => d.setMonth(d.getMonth() + 1); }
  const out: number[] = [];
  for (let i = 0; d.getTime() <= max && i < 100; i++, step()) if (d.getTime() >= min) out.push(d.getTime());
  return out;
}

/** Label of an x tick for the visible window min..max (zoomed or not): dates beyond 2 days, else HH:mm. */
export function visibleTimeTick(min: number, max: number, val: number | string): string | null {
  if (max - min > 2 * 24 * 3600 * 1000) return timeAxisTick(min, max)(val);
  const d = new Date(val as number);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** x-axis tick label for a rolling 24h window: "Jetzt" / "–12h" / "–24h", else null. */
export function relativeDayTick(val: number | string): string | null {
  const diff = Date.now() - (val as number);
  const h24 = 24 * 3600 * 1000;
  const h12 = 12 * 3600 * 1000;
  if (diff < 5 * 60 * 1000)                   return document.documentElement.lang === 'de' ? 'Jetzt' : 'Now';   // <html lang> = UI language
  if (Math.abs(diff - h12) < 30 * 60 * 1000)  return '–12h';
  if (Math.abs(diff - h24) < 30 * 60 * 1000)  return '–24h';
  return null;
}
